import React from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/**
 * Generic glass-style card wrapper with optional "Add" action, optional "Lookup" button, and optional action button.
 */
function GlassCard({ title, icon, children, onAdd, addLabel = "Add", onLookup, lookupLabel, onAction, actionLabel, actionIcon, isAI = false }) {
  const categoryName = title.replace(/\s+/g, "_").toLowerCase();

  return (
    <Paper 
      sx={{ p: 2, height: "fit-content", width: "100%" }}
      data-testid={`${categoryName}-card`}
      data-card-title={title}
      data-card-category={categoryName}
      data-item-count={React.Children.count(children)}
      data-context="patient_chart"
      data-timestamp={Date.now()}
    >
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0} flex="0 1 auto">
          <Box
            component="span"
            aria-hidden="true"
            data-testid={`${categoryName}-icon`}
            sx={{ display: "inline-flex", alignItems: "center", p: 0.5 }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {title}
          </Typography>
        </Box>
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1}
          flexWrap="wrap"
          sx={{ 
            minWidth: 0,
            flex: "0 1 auto",
            justifyContent: "flex-end"
          }}
        >
          {isAI && (
            <Chip
              icon={<AutoAwesomeIcon />}
              label="AI"
              size="small"
              variant="outlined"
              data-testid={`${categoryName}-ai-chip`}
              aria-label={`${title} powered by AI`}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                background:
                  "linear-gradient(135deg, rgba(138, 43, 226, 0.1), rgba(0, 191, 255, 0.1))",
              }}
            />
          )}
          {onLookup && (
            <Tooltip title={lookupLabel || "Lookup"}>
              <Button
                size="small"
                variant="outlined"
                onClick={onLookup}
                startIcon={<SearchIcon />}
                data-testid={`${categoryName}-lookup-button`}
                aria-label={lookupLabel}
                sx={{ 
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content"
                }}
              >
                {lookupLabel}
              </Button>
            </Tooltip>
          )}
          {onAction && (
            <Tooltip title={actionLabel || "Action"}>
              <Button
                size="small"
                variant="outlined"
                onClick={onAction}
                startIcon={actionIcon}
                data-testid={`${categoryName}-action-button`}
                aria-label={actionLabel}
                sx={{ 
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content"
                }}
              >
                {actionLabel}
              </Button>
            </Tooltip>
          )}
          {onAdd && (
            <Button
              size="small"
              variant="outlined"
              onClick={onAdd}
              color="primary"
              startIcon={<AddIcon />}
              aria-label={addLabel || `Add new ${title.replace(/s$/, '')}`}
              data-testid={`add-${categoryName}-button`}
              data-category={categoryName}
              data-action="add"
              data-button-label={addLabel || `Add ${title.replace(/s$/, '')}`}
              sx={{ 
                textTransform: "none",
                whiteSpace: "nowrap",
                minWidth: "fit-content"
              }}
            >
              {addLabel}
            </Button>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Paper>
  );
}

GlassCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node,
  onAdd: PropTypes.func,
  addLabel: PropTypes.string,
  onLookup: PropTypes.func,
  lookupLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionLabel: PropTypes.string,
  actionIcon: PropTypes.node,
  isAI: PropTypes.bool,
};

export default GlassCard;
